/**
 * Estado de uma lista paginada do cadastro: busca (com debounce), paginação e
 * carregamento. Extrai a repetição que hoje se copia em cada tela (`Cadastro.tsx`,
 * `CadastroSimples.tsx`) para um único lugar.
 *
 * Não introduz camada de dados nova (o projeto não usa TanStack Query): é `useState`
 * + `api`, só que encapsulado. O debounce evita uma requisição por tecla; a
 * paginação volta ao início quando a busca muda, porque a página 3 de um resultado
 * não corresponde à página 3 de outro.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { listarRecurso } from '@/lib/cadastroApi';
import type { Filtro } from '@/components/crud/FiltrosAvancados';

const POR_PAGINA = 50;
const DEBOUNCE_MS = 300;

export function useListaRecurso<T>(endpoint: string) {
  const [busca, setBusca] = useState('');
  const [tag, setTag] = useState(''); // id da tag do filtro (vazio = sem filtro)
  const [filtros, setFiltros] = useState<Filtro[]>([]); // condições avançadas (campo/op/valor)
  const [pagina, setPagina] = useState(0); // base 0 — vira offset = pagina * POR_PAGINA
  const [itens, setItens] = useState<T[]>([]);
  const [total, setTotal] = useState(0);
  const [carregando, setCarregando] = useState(true);

  // Guarda a última busca aplicada para debouce; um ref evita recriar o efeito.
  const debounce = useRef<ReturnType<typeof setTimeout>>(undefined);

  const carregar = useCallback(
    async (termo: string, pag: number, tagId: string, conds: Filtro[]) => {
      setCarregando(true);
      const r = await listarRecurso<T>(endpoint, {
        busca: termo,
        tag: tagId || undefined,
        // Só serializa quando há condição: um `filtros=[]` na URL é ruído em todo request.
        filtros: conds.length ? JSON.stringify(conds) : undefined,
        limite: POR_PAGINA,
        offset: pag * POR_PAGINA,
      });
      setCarregando(false);
      if (!r.ok) return void toast.error(r.erro);
      setItens(r.dados.itens);
      setTotal(r.dados.total);
    },
    [endpoint],
  );

  // Recarrega quando o endpoint muda (troca de entidade): zera busca, tag e página.
  useEffect(() => {
    setBusca('');
    setTag('');
    setFiltros([]);
    setPagina(0);
    void carregar('', 0, '', []);
  }, [carregar]);

  function buscar(termo: string) {
    setBusca(termo);
    setPagina(0);
    clearTimeout(debounce.current);
    debounce.current = setTimeout(() => void carregar(termo, 0, tag, filtros), DEBOUNCE_MS);
  }

  function filtrarPorTag(tagId: string) {
    setTag(tagId);
    setPagina(0);
    void carregar(busca, 0, tagId, filtros);
  }

  /** Aplica as condições avançadas. Volta à página 1: a página 3 de outro recorte não é esta. */
  function aplicarFiltros(conds: Filtro[]) {
    setFiltros(conds);
    setPagina(0);
    void carregar(busca, 0, tag, conds);
  }

  function irParaPagina(pag: number) {
    setPagina(pag);
    void carregar(busca, pag, tag, filtros);
  }

  /** Recarrega a página atual (após criar/editar/excluir). */
  const recarregar = useCallback(
    () => carregar(busca, pagina, tag, filtros),
    [carregar, busca, pagina, tag, filtros],
  );

  return {
    busca,
    buscar,
    tag,
    filtrarPorTag,
    filtros,
    aplicarFiltros,
    itens,
    total,
    carregando,
    pagina,
    porPagina: POR_PAGINA,
    irParaPagina,
    recarregar,
  };
}
